import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getEvents, 
  cancelEventRegistration, 
  getUserEventRegistrations,
  Event, 
  EventRegistration 
} from '../../services/eventService';
import { getSession, User } from '../../services/api';
import { EventForm } from '../../components/EventForm';
import { EventCard } from '../../components/EventCard';
import { LocationModal } from '../../components/LocationModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';  // Import the CSS for toasts

import styles from './EventOverviewPage.module.css';

interface EventOverviewProps {}

/**
 * Event Overview Page
 * 
 * Zeigt Events basierend auf Benutzerrolle:
 * - Admin/Event Admin/Event Reader: Alle Events
 * - Normale Benutzer: Nur Events, für die sie registriert sind
 * 
 * Die Filterung erfolgt automatisch im Backend.
 */
export const Event_Overview_Page: React.FC<EventOverviewProps> = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [showPastEvents, setShowPastEvents] = useState<boolean>(false);
  const navigate = useNavigate();

  // Fetch user session, events, and registrations
  useEffect(() => {
    const fetchUserAndEvents = async () => {
      setLoading(true);
      try {
        // Parallel laden: Session, Events und Registrierungen gleichzeitig
        const [sessionResponse, eventsData, registrationsData] = await Promise.all([
          getSession(),
          getEvents(), // Backend filtert bereits nach Rolle
          getUserEventRegistrations() // Nur eigene Registrierungen
        ]);

        // User-Session verarbeiten
        if (sessionResponse && sessionResponse.authenticated && sessionResponse.user) {
          setUser(sessionResponse.user);
          if (sessionResponse.roles && Array.isArray(sessionResponse.roles)) {
            setRoles(sessionResponse.roles);
          }
        }

        // Events setzen (bereits vom Backend gefiltert)
        setEvents(eventsData);
        
        // Registrierungen setzen
        setRegistrations(registrationsData);

      } catch (err) {
        setError('Failed to load events. Please try again later.');
        console.error('Error loading events:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndEvents();
  }, []);

  // Initialize Bootstrap modal properly
  useEffect(() => {
    // Ensure modal is properly closed when hidden
    const eventModal = document.getElementById('eventModal');
    if (eventModal) {
      eventModal.addEventListener('hidden.bs.modal', () => {
        // Reset form when modal is closed
        // This is a backup in case the form isn't reset properly
        const form = document.getElementById('eventForm') as HTMLFormElement;
        if (form) {
          form.reset();
        }
      });
    }
  }, []);

  // Refresh events list and registrations
  const refreshEvents = useCallback(async () => {
    try {
      // Parallel laden für bessere Performance
      const [eventsData, registrationsData] = await Promise.all([
        getEvents(),
        getUserEventRegistrations()
      ]);
      
      setEvents(eventsData);
      setRegistrations(registrationsData);
    } catch (err) {
      console.error('Error refreshing events:', err);
    }
  }, []);

  // Filter events by future/past status
  const futureEvents = events.filter(event => event.is_future);
  const pastEvents = events.filter(event => !event.is_future);

  // Handle event registration cancellation - using useCallback to avoid recreating the function on each render
  const handleCancelEvent = useCallback(async (eventId: string) => {
    try {
      const success = await cancelEventRegistration(eventId);
      if (success) {
        // Refresh events list
        await refreshEvents();
        toast.success('Event participation cancelled successfully.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      } else {
        toast.error('Failed to cancel event participation. Please try again.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
    } catch (err) {
      console.error('Error cancelling event:', err);
      toast.error('An error occurred while cancelling event participation.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    }
  }, [refreshEvents]);

  // Handler for copying registration link directly without modal
  const handleCopyRegistrationLink = useCallback((link: string) => {
    navigator.clipboard.writeText(link)
      .then(() => {
        toast.success('Registration link copied to clipboard!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        
        // Refresh the page to show updated data
        refreshEvents();
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
        toast.error('Failed to copy link. Please try again.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      });
  }, [refreshEvents]);

  // No need to set up event handlers for modals since we're using toasts directly

  // Check if user is admin
  const isAdmin = roles.some(role => ['Admin', 'Event Admin', 'Event Reader'].includes(role));
  const canCreateEvent = roles.some(role => ['Admin', 'Event Admin'].includes(role));

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={`container ${styles.loadingContainer}`}>
        <div className={`spinner-border text-primary ${styles.loadingSpinner}`} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h3>Loading events...</h3>
      </div>
    );
  }

  if (error) {
    return <div className={`container mt-4 text-danger ${styles.errorContainer}`}>{error}</div>;
  }

  return (
    <div className="container mt-4">
      {/* Toast Container with custom settings */}
            
      {/* Breadcrumb Navigation */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item active"><a>Events</a></li>
        </ol>
      </nav>

      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Speed Dating Events</h1>
          <p className={styles.pageSubtitle}>
            Discover and register for upcoming speed dating events.
          </p>
        </div>
        {canCreateEvent && (
          <button
            type="button"
            className={`${styles.actionButton} ${styles.primaryAction}`}
            data-bs-toggle="modal"
            data-bs-target="#eventModal"
          >
            <i className="bi bi-plus-lg"></i>
            Create Event
          </button>
        )}
      </header>

      <div className={`card p-4 role-container ${styles.roleContainer}`}>
        <div className="row">
          <div className="col-12 col-md-6">
            <h3 className="mb-0">Upcoming Events</h3>
          </div>
        </div>

        {/* Event Cards */}
        <div className={`row mt-3 g-3 ${styles.equalHeight}`}>
          {futureEvents.length > 0 ? (
            futureEvents.map(event => (
              <EventCard 
                key={event.Events_ID}
                event={event}
                registrations={registrations}
                userId={user?.User_ID}
                isAdmin={isAdmin}
                onCancelEvent={handleCancelEvent}
              />
            ))
          ) : (
            <div className="col-12 mt-3">
              <p>No upcoming events found.</p>
            </div>
          )}
        </div>

        <div className="row">
          <div className="col-12">
            <div className={styles.sectionToolbar}>
              <button
                type="button"
                className={`${styles.actionButton} ${styles.ghostAction} ${showPastEvents ? styles.ghostActionActive : ''}`}
                onClick={() => setShowPastEvents(!showPastEvents)}
              >
                <i className="bi bi-clock-history"></i>
                {showPastEvents ? 'Hide past events' : 'Show past events'}
              </button>
            </div>
          </div>
        </div>

        {showPastEvents && (
          <div className={`row g-3 ${styles.equalHeight}`}>
            {pastEvents.length > 0 ? (
              pastEvents.map(event => (
                <EventCard 
                  key={event.Events_ID}
                  event={event}
                  registrations={registrations}
                  userId={user?.User_ID}
                  isAdmin={isAdmin}
                  onCancelEvent={handleCancelEvent}
                />
              ))
            ) : (
              <div className="col-12">
                <p>No past events found.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Event Creation Modal */}
      {canCreateEvent && (
        <div className="modal fade" id="eventModal" tabIndex={-1} aria-labelledby="eventModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-lg modal-dialog-centered" style={{ display: 'flex', alignItems: 'center', minHeight: 'calc(100% - 1rem)' }}>
            <div className={`modal-content ${styles.modalContent}`}>
              <div className={`modal-header ${styles.modalHeader}`}>
                <div className={styles.modalHeaderGroup}>
                  <span className={styles.modalHeaderIcon}>
                    <i className="bi bi-calendar3"></i>
                  </span>
                  <div>
                    <h5 className={styles.modalTitle}>Create New Event</h5>
                    <p className={styles.modalSubtitle}>Plan your speed dating event with all the details.</p>
                  </div>
                </div>
                <button type="button" className={`btn-close ${styles.modalClose}`} data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className={`modal-body ${styles.modalBody}`}>
                <EventForm onEventCreated={refreshEvents} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Location Modal */}
      <LocationModal />
    </div>
  );
};




